import React, { useEffect, useRef, useState } from "react";

import { ReactComponent as CloseIcon } from "@common/assets/images/close-icon-bold.svg";
import TestingCoverageAggregationFormula from "@common/assets/images/releases/ab_aggregation_formula.png";
import { ReactComponent as AddIcon } from "@common/assets/images/shared-icons/plus-icon-v2.svg";
import Button from "@common/components/Button/Button";
import Heading from "@common/components/Heading/Heading";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import { ToggleButton } from "@common/components/ToggleButton/ToggleButton";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import useTestingCoverageScoring, { DimensionRow } from "@main/pages/Release/consumers/useTestingCoverageScoring";
import { useTestingCoverageSearchPopover } from "@main/pages/Release/consumers/useTestingCoverageSearchPopover";
import { getMetricDimensionConfig } from "@main/pages/Release/helpers/metricDimensionConfig";
import { MetricScoreOutletContext } from "@main/pages/Release/views/MetricDetails/ReleaseMetricDetailsLayout";
import { MetricRow } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";
import { BasicsPopoverSearch } from "@main/pages/shared/components/BasicsPopoverSearch/BasicsPopoverSearch";
import { MetricKeys, RoutePaths } from "@main/router/config/routePaths";
import * as MetricDetailsStyled from "main/pages/Release/views/MetricDetails/MetricDetails.styles";
import * as Styled from "main/pages/Release/views/MetricDetails/TestingCoverage/TestingCoverageScoreView.styles";
import { generatePath, Link, useOutletContext, useSearchParams } from "react-router-dom";

export default function TestingCoverageScoreView() {
  const [searchParams] = useSearchParams();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const { refetchReleaseStatus } = useOutletContext<MetricScoreOutletContext>();

  const {
    rows,
    toggleDimension,
    addCategory,
    removeCategory,
    setMinimum,
    commitMinimum,
    weightA,
    setWeightA,
    weightB,
    setWeightB,
    weightError,
    scoringConfigLoading,
    handleSave,
    isSaving,
  } = useTestingCoverageScoring(refetchReleaseStatus);

  const metadataDimensionsPath = generatePath(RoutePaths.RELEASE_METRIC_METADATA_DIMENSIONS, {
    workspaceSlug: selectedWorkspace?.slug ?? null,
    appKey: selectedApp?.key ?? null,
    metricKey: MetricKeys.testingCoverage,
  });

  if (scoringConfigLoading) {
    return <Spinner />;
  }

  return (
    <div style={{ padding: "3.2rem 0" }}>
      <MetricDetailsStyled.MetricsCard>
        <Heading
          heading3
          marginBottom={24}
        >
          Score
        </Heading>

        <Paragraph marginBottom={24}>
          {
            "The 'Coverage' score measures how broadly testers exercise dimensions (OS, device, your own metadata etc.) you care about during the release."
          }
        </Paragraph>

        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Dimensions you want to see tested
        </Heading>

        <Paragraph marginBottom={24}>
          For each enabled dimension, define which categories you expect to be tested and how much, e.g. “Minimum 10
          sessions with the en-US locale”.
          <br />
          {"Only the enabled "}
          <Link
            to={{ pathname: metadataDimensionsPath, search: searchParams.toString() }}
            style={{ textDecoration: "underline" }}
          >
            metadata dimension
          </Link>
          {" are shown."}
        </Paragraph>

        <Styled.DimensionList>
          {rows.map((row: DimensionRow) => (
            <DimensionSection
              key={row.key}
              row={row}
              onToggle={() => toggleDimension(row.key)}
              onAddCategory={(value) => addCategory(row.key, value)}
              onRemoveCategory={(value) => removeCategory(row.key, value)}
              onMinimumChange={(value, minimum) => setMinimum(row.key, value, minimum)}
              onCommitMinimum={commitMinimum}
            />
          ))}
        </Styled.DimensionList>

        <Paragraph marginBottom={24}>
          {"Each dimension is scored from 0 to 10 based on how many sessions were actually done within each category.\n" +
            "Then, all those dimension scores are collected and the final ‘Coverage’ score is calculated:"}
        </Paragraph>

        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Aggregation
        </Heading>

        <img
          style={{ marginBottom: "1.6rem" }}
          width={360}
          src={TestingCoverageAggregationFormula}
          alt="Formula"
        />

        <Styled.ThresholdList>
          <MetricRow
            label="a"
            value={weightA}
            onChange={setWeightA}
            description="The percentage weight of the lowest-scoring dimension on the Coverage score. The goal is to penalize a single weak spot, as one poorly tested dimension can jeopardize the entire release. ← System default: 60%."
          />
          <MetricRow
            label="b"
            value={weightB}
            isError={!!weightError}
            onChange={setWeightB}
            description="The percentage weight of the average dimension score on the Coverage score. The sum of a + b must equal 100%. ← System default: 40%."
            error={weightError}
          />
        </Styled.ThresholdList>

        <Button
          type="submit"
          size="small"
          disabled={!!weightError || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </MetricDetailsStyled.MetricsCard>
    </div>
  );
}

interface DimensionSectionProps {
  row: DimensionRow;
  onToggle: () => void;
  onAddCategory: (value: string) => void;
  onRemoveCategory: (value: string) => void;
  onMinimumChange: (value: string, minimum: number) => void;
  onCommitMinimum: () => void;
}

function DimensionSection({
  row,
  onToggle,
  onAddCategory,
  onRemoveCategory,
  onMinimumChange,
  onCommitMinimum,
}: DimensionSectionProps) {
  const { label, icon } = getMetricDimensionConfig(row.key);
  const popover = useTestingCoverageSearchPopover({
    attributeName: row.searchKey,
  });
  const [lastAddedValue, setLastAddedValue] = useState<string | null>(null);

  const onPopoverValueSelected = (value: string) => {
    popover.setQuery("");

    if (!value) return;
    popover.setOpen(false);
    onAddCategory(value);
    setLastAddedValue(value);
  };

  return (
    <Styled.DimensionSection>
      {row.kind === "dimension" ? (
        <Tooltip
          style={{ width: "auto" }}
          text={`Enable or disable the ${label} dimension.`}
          position="top"
        >
          <ToggleButton
            checked={row.enabled}
            onChange={onToggle}
          />
        </Tooltip>
      ) : (
        <Tooltip
          style={{ width: "auto" }}
          text={`Visit "Metadata dimensions" screen to disable this dimension`}
          position="top"
        >
          <ToggleButton
            disabled={true}
            checked={true}
          />
        </Tooltip>
      )}

      <Styled.DimensionData $disabled={!row.enabled}>
        <Styled.DimensionIconWrapper>{icon}</Styled.DimensionIconWrapper>
        <Styled.DimensionName p2>{label}</Styled.DimensionName>
        <Styled.CategoryRows>
          {row.targets.map((item) => (
            <CategoryChip
              key={item.value}
              name={item.value}
              minimum={item.minimum}
              focusInput={item.value === lastAddedValue}
              onRemoveClick={() => onRemoveCategory(item.value)}
              onInputChange={(value) => onMinimumChange(item.value, value)}
              onInputCommit={onCommitMinimum}
            />
          ))}

          <Styled.AddCategoryButton
            ref={popover.reference}
            {...popover.getReferenceProps()}
          >
            <AddIcon />
          </Styled.AddCategoryButton>
        </Styled.CategoryRows>
      </Styled.DimensionData>

      {popover.open && (
        <BasicsPopoverSearch
          query={popover.query}
          setQuery={popover.setQuery}
          error={popover.error}
          loading={popover.loading}
          results={popover.availableFilterHits}
          onValueSelected={onPopoverValueSelected}
          popover={popover}
        />
      )}
    </Styled.DimensionSection>
  );
}

interface CategoryChipProps {
  name: string;
  minimum?: number;
  image?: React.ReactNode;
  focusInput?: boolean;
  onRemoveClick?: () => void;
  onInputChange?: (value: number) => void;
  onInputCommit?: () => void;
}

function CategoryChip({
  name,
  minimum,
  image,
  focusInput,
  onRemoveClick,
  onInputChange,
  onInputCommit,
}: CategoryChipProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(String(minimum ?? 0));
  const [isFocused, setIsFocused] = useState(false);
  const initialValueRef = useRef<number>(minimum ?? 0);

  useEffect(() => {
    if (!focusInput) return;
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [focusInput]);

  useEffect(() => {
    setText(String(minimum ?? 0));
  }, [minimum]);

  return (
    <Styled.CategoryChip>
      {image}
      <Tooltip
        style={{ width: "auto" }}
        text={`Edit category`}
        position="top"
      >
        <Styled.CategoryChipName>{name}</Styled.CategoryChipName>
      </Tooltip>

      <Tooltip
        style={{ width: "auto" }}
        text={isFocused ? `Minimum number of sessions:` : `Edit number of sessions`}
        position="top"
      >
        <Styled.CategoryChipMinimumInput
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={text}
          onFocus={() => {
            setIsFocused(true);
            initialValueRef.current = minimum ?? 0;
          }}
          onChange={(e) => {
            const raw = e.target.value;
            if (!/^\d*$/.test(raw)) return;
            setText(raw);

            const num = parseInt(raw, 10);
            if (!isNaN(num)) onInputChange?.(num);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") inputRef.current?.blur();
          }}
          onBlur={() => {
            setIsFocused(false);

            let committed: number;
            if (text.trim() === "") {
              committed = 0;
              setText("0");
              onInputChange?.(0);
            } else {
              const num = parseInt(text, 10);
              if (isNaN(num)) {
                committed = minimum ?? 0;
                setText(String(committed));
              } else {
                committed = num;
              }
            }

            if (committed !== initialValueRef.current) onInputCommit?.();
          }}
        />
      </Tooltip>

      <Tooltip
        style={{ width: "auto" }}
        text={`Remove category`}
        position="top"
      >
        <Styled.RemoveCategoryButton onClick={onRemoveClick}>
          <CloseIcon />
        </Styled.RemoveCategoryButton>
      </Tooltip>
    </Styled.CategoryChip>
  );
}
